// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Image, Modal, Platform, Pressable, StyleSheet, TextInput, View } from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import animalAvatar from 'animal-avatar-generator';
import { Text } from '@/components/Themed';
import { useUser } from '@/app/user-context';
import { useI18n } from '@/i18n';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { getSharedBffWebSocket } from '@/services/bffWebSocket';

type PlayerProfile = {
  id: string;
  name?: string;
  displayName?: string | null;
  imageKey?: string | null;
  imageThumbKey?: string | null;
};

type PendingUpload = {
  blob: Blob;
  contentType: string;
  fileName: string;
  fileSize: number;
};

type AvatarStyle = 'adventurer' | 'icons' | 'croodles' | 'animals';

export default function ProfileScreen() {
  const { user } = useUser();
  const { t } = useI18n();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const socketRef = useRef(getSharedBffWebSocket(apiBaseUrl));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [player, setPlayer] = useState<PlayerProfile | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [statusNote, setStatusNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [avatarSeed, setAvatarSeed] = useState<string | null>(null);
  const [avatarPickerOpen, setAvatarPickerOpen] = useState(false);
  const [avatarChoices, setAvatarChoices] = useState<
    Array<{ id: string; seed: string; style: AvatarStyle; retries: number }>
  >([]);
  const [avatarStyle, setAvatarStyle] = useState<AvatarStyle>('adventurer');
  const avatarRefreshLock = useRef(false);
  const pendingUploadRef = useRef<PendingUpload | null>(null);
  const imageRequestIdRef = useRef<string | null>(null);
  const updateRequestIdRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const resolvedImage =
    player?.imageThumbKey ||
    player?.imageKey ||
    (player?.id ? `https://api.dicebear.com/6.x/adventurer/png?seed=${encodeURIComponent(player.id)}` : null);

  const loadPlayer = useCallback(async () => {
    if (!user?.token || !user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiBaseUrl}/api/player/by-user/${user.id}`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      if (!response.ok) {
        throw new Error('profile.load.failed');
      }
      const payload = (await response.json()) as PlayerProfile;
      setPlayer(payload);
      setDisplayName(payload.displayName ?? payload.name ?? '');
    } catch {
      setError(t('profile.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, t, user?.id, user?.token]);

  useEffect(() => {
    loadPlayer();
  }, [loadPlayer]);

  useEffect(() => {
    const socket = socketRef.current;
    socket.setAuthToken(user?.token ?? null);
    const unsubscribe = socket.onMessage((message) => {
      const type = (message as { type?: string }).type;
      if (type === 'player.image.presign.ack') {
        const payload = message as { requestId?: string; uploadUrl?: string; publicUrl?: string };
        if (imageRequestIdRef.current && payload.requestId !== imageRequestIdRef.current) {
          return;
        }
        const pending = pendingUploadRef.current;
        if (!pending || !payload.uploadUrl || !payload.publicUrl) {
          imageRequestIdRef.current = null;
          pendingUploadRef.current = null;
          setUploading(false);
          setError(t('profile.uploadFailed'));
          return;
        }
        (async () => {
          try {
            const uploadResp = await fetch(payload.uploadUrl!, {
              method: 'PUT',
              headers: { 'Content-Type': pending.contentType },
              body: pending.blob,
            });
            if (!uploadResp.ok) {
              throw new Error(`Upload failed ${uploadResp.status}`);
            }
            const updateRequestId = generateId();
            updateRequestIdRef.current = updateRequestId;
            socketRef.current.send({
              type: 'player.profile.update',
              playerId: player?.id,
              requestId: updateRequestId,
              imageKey: payload.publicUrl,
            });
            setPlayer((prev) => (prev ? { ...prev, imageKey: payload.publicUrl, imageThumbKey: payload.publicUrl } : prev));
            setStatusNote(t('profile.uploadSuccess'));
          } catch {
            setError(t('profile.uploadFailed'));
          } finally {
            setUploading(false);
            imageRequestIdRef.current = null;
            pendingUploadRef.current = null;
          }
        })();
        return;
      }
      if (type === 'player.image.presign.error') {
        const payload = message as { requestId?: string; message?: string };
        if (imageRequestIdRef.current && payload.requestId !== imageRequestIdRef.current) {
          return;
        }
        imageRequestIdRef.current = null;
        pendingUploadRef.current = null;
        setUploading(false);
        setError(payload.message ?? t('profile.uploadFailed'));
        return;
      }
      if (type === 'player.update.ack') {
        const payload = message as { requestId?: string };
        if (updateRequestIdRef.current && payload.requestId !== updateRequestIdRef.current) {
          return;
        }
        updateRequestIdRef.current = null;
        setSaving(false);
        setStatusNote(t('profile.saveSuccess'));
        return;
      }
      if (type === 'player.update.error') {
        const payload = message as { requestId?: string; message?: string };
        if (updateRequestIdRef.current && payload.requestId !== updateRequestIdRef.current) {
          return;
        }
        updateRequestIdRef.current = null;
        setSaving(false);
        setError(payload.message ?? t('profile.saveFailed'));
        return;
      }
      if (type === 'players_snapshot') {
        const payload = message as { players?: PlayerProfile[]; Players?: PlayerProfile[] };
        const list = payload.players ?? payload.Players ?? [];
        const updated = list.find((p) => p.id === player?.id);
        if (updated) {
          setPlayer((prev) => ({ ...(prev ?? updated), ...updated }));
        }
      }
    });
    return () => {
      unsubscribe();
    };
  }, [player?.id, t, user?.token]);

  const sendProfileUpdate = useCallback(
    (nextName: string) => {
      if (!player?.id) return;
      setSaving(true);
      setError(null);
      setStatusNote(t('profile.saving'));
      const requestId = generateId();
      updateRequestIdRef.current = requestId;
      socketRef.current.setAuthToken(user?.token ?? null);
      socketRef.current.send({
        type: 'player.profile.update',
        playerId: player.id,
        requestId,
        displayName: nextName.trim(),
      });
    },
    [player?.id, t, user?.token]
  );

  const handleSaveName = useCallback(() => {
    const trimmed = displayName.trim();
    if (!player?.id) return;
    if (trimmed === (player.displayName ?? player.name ?? '')) return;
    sendProfileUpdate(trimmed);
  }, [displayName, player?.displayName, player?.id, player?.name, sendProfileUpdate]);

  const buildAvatarUrl = useCallback(
    (seed: string, styleOverride?: AvatarStyle) => {
      const style = styleOverride ?? avatarStyle;
      if (style === 'animals') {
        const svg = animalAvatar(seed, { size: 256 });
        return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
      }
      return `https://api.dicebear.com/6.x/${style}/png?seed=${encodeURIComponent(seed)}`;
    },
    [avatarStyle]
  );

  const generateAvatarChoices = useCallback(() => {
    setAvatarChoices(() =>
      Array.from({ length: 12 }, () => ({
        id: generateId(),
        seed: generateId(),
        style: avatarStyle,
        retries: 0,
      }))
    );
  }, [avatarStyle]);

  const handleAvatarLoadError = useCallback(
    (choiceId: string) => {
      setAvatarChoices((prev) =>
        prev.map((choice) => {
          if (choice.id !== choiceId) return choice;
          if (choice.retries >= 2) return choice;
          return {
            ...choice,
            seed: generateId(),
            retries: choice.retries + 1,
          };
        })
      );
    },
    []
  );

  useEffect(() => {
    if (!avatarPickerOpen) return;
    if (avatarRefreshLock.current) return;
    avatarRefreshLock.current = true;
    if (avatarChoices.length === 0 || avatarChoices[0]?.style !== avatarStyle) {
      generateAvatarChoices();
    }
    setTimeout(() => {
      avatarRefreshLock.current = false;
    }, 0);
  }, [avatarPickerOpen, avatarStyle, generateAvatarChoices, avatarChoices.length]);

  const openAvatarPicker = useCallback(() => {
    setAvatarStyle('adventurer');
    generateAvatarChoices();
    setAvatarPickerOpen(true);
  }, [generateAvatarChoices]);

  const requestPresign = useCallback(
    (payload: PendingUpload) => {
      if (!player?.id) return;
      pendingUploadRef.current = payload;
      const requestId = generateId();
      imageRequestIdRef.current = requestId;
      setUploading(true);
      setStatusNote(t('profile.uploading'));
      setError(null);
      socketRef.current.setAuthToken(user?.token ?? null);
      socketRef.current.send({
        type: 'player.image.presign',
        playerId: player.id,
        requestId,
        fileName: payload.fileName,
        contentType: payload.contentType,
        fileSize: payload.fileSize,
      });
    },
    [player?.id, t, user?.token]
  );

  const selectAvatar = useCallback(
    (seed: string) => {
      if (!player?.id) return;
      if (avatarStyle === 'animals') {
        const svg = animalAvatar(seed, { size: 256 });
        const blob = new Blob([svg], { type: 'image/svg+xml' });
        requestPresign({
          blob,
          contentType: 'image/svg+xml',
          fileName: `player-${player.id}-${seed}.svg`,
          fileSize: blob.size,
        });
        setAvatarPickerOpen(false);
        return;
      }
      setSaving(true);
      setError(null);
      setStatusNote(t('profile.saving'));
      setAvatarSeed(seed);
      const url = buildAvatarUrl(seed, avatarStyle);
      const requestId = generateId();
      updateRequestIdRef.current = requestId;
      socketRef.current.setAuthToken(user?.token ?? null);
      socketRef.current.send({
        type: 'player.profile.update',
        playerId: player.id,
        requestId,
        imageKey: url,
        imageSeed: seed,
      });
      setPlayer((prev) => (prev ? { ...prev, imageKey: url, imageThumbKey: url } : prev));
      setAvatarPickerOpen(false);
    },
    [avatarStyle, buildAvatarUrl, player?.id, requestPresign, t, user?.token]
  );

  const handleWebSelect = useCallback(
    (event: any) => {
      const file = event.target.files?.[0] as File | undefined;
      if (!file || !player?.id) return;
      requestPresign({
        blob: file,
        contentType: file.type || 'image/jpeg',
        fileName: file.name,
        fileSize: file.size,
      });
      event.target.value = '';
    },
    [player?.id, requestPresign]
  );

  const handleMobileSelect = useCallback(async () => {
    if (!player?.id) return;
    const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!permission.granted) {
      setError(t('profile.permissionDenied'));
      return;
    }
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      quality: 0.9,
    });
    if (result.canceled || result.assets.length === 0) return;
    const asset = result.assets[0];
    const response = await fetch(asset.uri);
    const blob = await response.blob();
    requestPresign({
      blob,
      contentType: blob.type || 'image/jpeg',
      fileName: asset.fileName ?? `player-${player.id}.jpg`,
      fileSize: blob.size,
    });
  }, [player?.id, requestPresign, t]);

  const uploadButtonPress = useCallback(() => {
    if (uploading) return;
    if (Platform.OS === 'web') {
      fileInputRef.current?.click();
    } else {
      void handleMobileSelect();
    }
  }, [handleMobileSelect, uploading]);

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.avatarWrap}>
          {resolvedImage ? (
            <Image source={{ uri: resolvedImage }} style={styles.avatar} resizeMode="cover" />
          ) : (
            <View style={styles.avatarPlaceholder} />
          )}
        </View>
        <View style={styles.headerContent}>
          <Text style={styles.title}>{t('profile.title')}</Text>
          {loading ? (
            <View style={styles.loadingRow}>
              <ActivityIndicator size="small" color="#93b6ff" />
              <Text style={styles.meta}>{t('profile.loading')}</Text>
            </View>
          ) : null}
          {statusNote ? <Text style={styles.meta}>{statusNote}</Text> : null}
          {error ? <Text style={styles.error}>{error}</Text> : null}
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.label}>{t('profile.nameLabel')}</Text>
        <TextInput
          value={displayName}
          onChangeText={setDisplayName}
          onBlur={handleSaveName}
          onSubmitEditing={handleSaveName}
          editable={!loading && !saving}
          placeholder={t('profile.namePlaceholder')}
          placeholderTextColor="#7f93c7"
          style={styles.input}
        />
        <Pressable style={[styles.primaryButton, saving && styles.primaryButtonDisabled]} onPress={handleSaveName}>
          <Text style={styles.primaryButtonText}>{t('profile.save')}</Text>
        </Pressable>
      </View>

      <View style={styles.section}>
        <Text style={styles.label}>{t('profile.imageLabel')}</Text>
        <View style={styles.buttonRow}>
          <Pressable
            style={[styles.secondaryButton, uploading && styles.primaryButtonDisabled]}
            onPress={uploadButtonPress}
            disabled={uploading}
          >
            <Text style={styles.secondaryButtonText}>{t('profile.uploadImage')}</Text>
          </Pressable>
          <Pressable style={styles.secondaryButton} onPress={openAvatarPicker}>
            <Text style={styles.secondaryButtonText}>{t('profile.selectAvatar')}</Text>
          </Pressable>
        </View>
      </View>

      {Platform.OS === 'web' ? (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleWebSelect}
          style={{ display: 'none' }}
        />
      ) : null}
      <Modal transparent visible={avatarPickerOpen} animationType="fade" onRequestClose={() => setAvatarPickerOpen(false)}>
        <Pressable style={styles.modalBackdrop} onPress={() => setAvatarPickerOpen(false)}>
          <View style={styles.avatarModalCard}>
            <Text style={styles.avatarModalTitle}>{t('profile.avatarPickerTitle')}</Text>
            <View style={styles.avatarTabs}>
              <Pressable
                style={[styles.avatarTab, avatarStyle === 'adventurer' && styles.avatarTabActive]}
                onPress={() => setAvatarStyle('adventurer')}
              >
                <Text style={styles.avatarTabText}>{t('profile.avatarPeople')}</Text>
              </Pressable>
              {Platform.OS === 'web' ? (
                <Pressable
                  style={[styles.avatarTab, avatarStyle === 'animals' && styles.avatarTabActive]}
                  onPress={() => setAvatarStyle('animals')}
                >
                  <Text style={styles.avatarTabText}>{t('profile.avatarAnimals')}</Text>
                </Pressable>
              ) : null}
              <Pressable
                style={[styles.avatarTab, avatarStyle === 'icons' && styles.avatarTabActive]}
                onPress={() => setAvatarStyle('icons')}
              >
                <Text style={styles.avatarTabText}>{t('profile.avatarIcons')}</Text>
              </Pressable>
              <Pressable
                style={[styles.avatarTab, avatarStyle === 'croodles' && styles.avatarTabActive]}
                onPress={() => setAvatarStyle('croodles')}
              >
                <Text style={styles.avatarTabText}>{t('profile.avatarCroodles')}</Text>
              </Pressable>
            </View>
            <View style={styles.avatarGrid}>
              {avatarChoices.length === 0 ? (
                <Text style={styles.avatarLoading}>{t('profile.avatarLoading')}</Text>
              ) : (
                avatarChoices.map(({ id, seed, style }) => {
                  const uri = buildAvatarUrl(seed, style);
                  const handleError = style === 'animals' ? undefined : () => handleAvatarLoadError(id);
                  return (
                    <Pressable key={id} style={styles.avatarChoice} onPress={() => selectAvatar(seed)}>
                      {style === 'animals' && Platform.OS === 'web' ? (
                        // Web-only SVG injection for animal avatars.
                        <div
                          style={styles.avatarChoiceSvg as unknown as React.CSSProperties}
                          dangerouslySetInnerHTML={{ __html: animalAvatar(seed, { size: 256 }) }}
                        />
                      ) : (
                        <Image source={{ uri }} style={styles.avatarChoiceImage} onError={handleError} />
                      )}
                    </Pressable>
                  );
                })
              )}
            </View>
            <View style={styles.avatarModalActions}>
              <Pressable style={styles.moreButton} onPress={generateAvatarChoices}>
                <Text style={styles.moreButtonText}>{t('profile.avatarMore')}</Text>
              </Pressable>
              <Pressable style={styles.closeButton} onPress={() => setAvatarPickerOpen(false)}>
                <Text style={styles.closeButtonText}>{t('profile.close')}</Text>
              </Pressable>
            </View>
          </View>
        </Pressable>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
    gap: 16,
    backgroundColor: '#0b1224',
  },
  header: {
    flexDirection: 'row',
    gap: 16,
    alignItems: 'center',
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#1e2d55',
    backgroundColor: '#101a34',
  },
  avatarWrap: {
    width: 88,
    height: 88,
    borderRadius: 16,
    overflow: 'hidden',
    backgroundColor: '#0b1224',
    borderWidth: 1,
    borderColor: '#1f2a4a',
  },
  avatar: {
    width: '100%',
    height: '100%',
  },
  avatarPlaceholder: {
    flex: 1,
    backgroundColor: '#172344',
  },
  headerContent: {
    flex: 1,
    gap: 6,
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
    color: '#e5edff',
  },
  meta: {
    color: '#93b6ff',
    fontSize: 12,
  },
  error: {
    color: '#fca5a5',
    fontSize: 12,
  },
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  section: {
    gap: 10,
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#1e2d55',
    backgroundColor: '#0f1933',
  },
  label: {
    color: '#bcd3ff',
    fontSize: 13,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  input: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#2a3a66',
    paddingHorizontal: 12,
    paddingVertical: 10,
    color: '#e5edff',
    fontSize: 14,
    backgroundColor: '#0b1224',
  },
  primaryButton: {
    alignSelf: 'flex-start',
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 10,
    backgroundColor: '#25408a',
  },
  primaryButtonDisabled: {
    opacity: 0.6,
  },
  primaryButtonText: {
    color: '#e5edff',
    fontWeight: '700',
  },
  buttonRow: {
    flexDirection: 'row',
    gap: 10,
    flexWrap: 'wrap',
  },
  secondaryButton: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#2a3a66',
    backgroundColor: '#0b1224',
  },
  secondaryButtonText: {
    color: '#c6d7ff',
    fontWeight: '600',
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(7,12,24,0.65)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  avatarModalCard: {
    width: '100%',
    maxWidth: 520,
    backgroundColor: '#0b1224',
    borderRadius: 16,
    padding: 16,
    borderWidth: 1,
    borderColor: '#24346b',
  },
  avatarModalTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#e5edff',
    marginBottom: 12,
  },
  avatarGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  avatarLoading: {
    color: '#c6d7ff',
    fontSize: 12,
  },
  avatarTabs: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 12,
  },
  avatarTab: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#2a3a66',
    backgroundColor: '#0b1224',
  },
  avatarTabActive: {
    backgroundColor: '#25408a',
    borderColor: '#3a5ab0',
  },
  avatarTabText: {
    color: '#c6d7ff',
    fontWeight: '600',
    fontSize: 12,
  },
  avatarChoice: {
    width: 64,
    height: 64,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#2a3a66',
    overflow: 'hidden',
    backgroundColor: '#101a34',
  },
  avatarChoiceImage: {
    width: '100%',
    height: '100%',
  },
  avatarChoiceSvg: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarModalActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 16,
  },
  moreButton: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#2a3a66',
    backgroundColor: '#0b1224',
  },
  moreButtonText: {
    color: '#c6d7ff',
    fontWeight: '600',
  },
  closeButton: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 10,
    backgroundColor: '#25408a',
  },
  closeButtonText: {
    color: '#e5edff',
    fontWeight: '700',
  },
});

function generateId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return (crypto as any).randomUUID();
  }
  return `req_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}
